import React from 'react';
import {useRoutes} from 'react-router-dom';
import {CssBaseline, ThemeProvider, Snackbar} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import ThemeSettings from './layouts/full-layout/customizer/ThemeSettings';
import Router from './routes/Router';
import {SocketProvider, SocketContext} from './context/SocketContext';
import {useSelector} from "react-redux";
import {useAgentPresenceHeartbeat} from "./hooks/useAgentPresenceHeartbeat";

const GatewayStatusBanner = () => {
    const {gatewayOnline} = React.useContext(SocketContext);
    const prevRef = React.useRef(null);
    const hasEverBeenOnlineRef = React.useRef(false);
    const disconnectTimerRef = React.useRef(null);
    const [showDisconnected, setShowDisconnected] = React.useState(false);
    const [showReconnected, setShowReconnected] = React.useState(false);

    React.useEffect(() => {
        const wasOnline = prevRef.current;
        prevRef.current = gatewayOnline;

        if (gatewayOnline === true) {
            if (wasOnline === false && hasEverBeenOnlineRef.current) {
                clearTimeout(disconnectTimerRef.current);
                setShowDisconnected(false);
                setShowReconnected(true);
            }
            hasEverBeenOnlineRef.current = true;
        }

        if (gatewayOnline === false && hasEverBeenOnlineRef.current) {
            disconnectTimerRef.current = setTimeout(() => setShowDisconnected(true), 4000);
        }

        return () => clearTimeout(disconnectTimerRef.current);
    }, [gatewayOnline]);

    return (
        <>
            <Snackbar
                open={showDisconnected}
                anchorOrigin={{vertical: 'top', horizontal: 'center'}}
            >
                <MuiAlert elevation={6} variant="filled" severity="warning">
                    Conexión con el servidor inestable — reconectando...
                </MuiAlert>
            </Snackbar>
            <Snackbar
                open={showReconnected}
                autoHideDuration={4000}
                onClose={() => setShowReconnected(false)}
                anchorOrigin={{vertical: 'top', horizontal: 'center'}}
            >
                <MuiAlert elevation={6} variant="filled" severity="success" onClose={() => setShowReconnected(false)}>
                    Conexión restablecida
                </MuiAlert>
            </Snackbar>
        </>
    );
};

const App = () => {
    const routing = useRoutes(Router);
    const theme = ThemeSettings();
    const [isOnline, setIsOnline] = React.useState(window.navigator.onLine);
    const [showOfflineSnackbar, setShowOfflineSnackbar] = React.useState(false);
    const [showOnlineSnackbar, setShowOnlineSnackbar] = React.useState(false);
    const [isFocused, setIsFocused] = React.useState(document.hasFocus());
    const agent = useSelector(state => state.agent);

    useAgentPresenceHeartbeat(agent, {
        intervalMs: 90000,
        enabled: Boolean(agent?.email && agent?.id_customer),
    });


    React.useEffect(() => {
        function handleOnline() {
            setIsOnline(true);
            setShowOnlineSnackbar(true);
            setTimeout(() => setShowOnlineSnackbar(false), 3000); // Ocultar Snackbar después de 3 segundos
            window.location.href = "/admin/chat"; // Recargar la página cuando se restablezca la conexión
        }

        function handleOffline() {
            setIsOnline(false);
            setShowOfflineSnackbar(true);
        }

        function handleVisibilityChange() {
            setIsFocused(document.hasFocus());
        }

        function handleFocus() {
            setIsFocused(true);
        }

        function handleBlur() {
            setIsFocused(false);
        }

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleFocus);
        window.addEventListener('blur', handleBlur);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleFocus);
            window.removeEventListener('blur', handleBlur);
        };
    }, []);

    const handleCloseOfflineSnackbar = () => {
        setShowOfflineSnackbar(false);
    };

    const handleCloseOnlineSnackbar = () => {
        setShowOnlineSnackbar(false);
    };
    return (
        <SocketProvider>
            <ThemeProvider theme={theme}>
                <CssBaseline/>
                {routing}
                <GatewayStatusBanner/>
                <Snackbar
                    open={showOfflineSnackbar && !isOnline}
                    autoHideDuration={6000}
                    onClose={handleCloseOfflineSnackbar}
                >
                    <MuiAlert
                        elevation={6}
                        variant="filled"
                        severity="error"
                        onClose={handleCloseOfflineSnackbar}
                    >
                        ¡Se ha perdido la conexión a Internet!
                    </MuiAlert>
                </Snackbar>
                <Snackbar
                    open={showOnlineSnackbar && isOnline && isFocused}
                    autoHideDuration={6000}
                    onClose={handleCloseOnlineSnackbar}
                >
                    <MuiAlert
                        elevation={6}
                        variant="filled"
                        severity="success"
                        onClose={handleCloseOnlineSnackbar}
                    >
                        ¡La conexión a Internet se ha restablecido! La página se recargará automáticamente.
                    </MuiAlert>
                </Snackbar>
            </ThemeProvider>
        </SocketProvider>
    );
};

export default App;
